import Swal from "sweetalert2";

const COLLECTIONS = Object.freeze({
  questions: { filter: "filter_questions", remove: "delete_question", idKey: "question_id" },
  sections: { filter: "filter_sections", remove: "delete_section", idKey: "section_id" },
  forms: { filter: "filter_forms", remove: "delete_form", idKey: "form_id" },
});

function get_collection_config(collection) {
  if (!Object.hasOwn(COLLECTIONS, collection)) throw new Error("Invalid collection");
  return Reflect.get(COLLECTIONS, collection);
}

function get_collection_items(response, collection) {
  get_collection_config(collection);
  return Reflect.get(response?.data || {}, collection) || [];
}

function get_delete_payload(collection, id) {
  const payload = {};
  Reflect.set(payload, get_collection_config(collection).idKey, id);
  return payload;
}

function show_success(title) {
  Swal.fire({ icon: "success", title, toast: true, position: "bottom-end",
    timer: 1800, showConfirmButton: false });
}

function assign_collection(context, collection, items) {
  get_collection_config(collection);
  Reflect.set(context, collection, items);
}

function remove_collection_item(context, collection, id) {
  get_collection_config(collection);
  const items = Reflect.get(context, collection) || [];
  Reflect.set(context, collection, items.filter(item => item.id !== id));
}

export default {
  load_setup_data() {
    this.loading = true;
    Promise.allSettled([
      this.fetch_collection("questions"),
      this.fetch_collection("sections"),
      this.fetch_collection("forms"),
      this.fetch_question_types(),
      this.fetch_profile_types(),
    ]).finally(() => { this.loading = false; });
  },
  fetch_collection(collection) {
    const config = get_collection_config(collection);
    return $.ajax({
      url: `${process.env.VUE_APP_BACKEND_API_URL}/membership_management/${config.filter}/`,
      method: "POST",
      headers: this.get_json_headers(),
    }).then(response => {
      const items = get_collection_items(response, collection);
      assign_collection(this, collection, items);
      if (collection === "forms" && this.forms.length && !this.selected_preview_form_id) {
        this.selected_preview_form_id = this.forms[0].id;
        this.load_form_preview();
      }
    });
  },
  fetch_question_types() {
    return $.ajax({
      url: `${process.env.VUE_APP_BACKEND_API_URL}/membership_management/get_question_types/`,
      method: "GET",
      headers: this.get_headers(),
    }).then(response => {
      this.question_types = response?.data?.question_types || response?.data || [];
    });
  },
  fetch_profile_types() {
    return $.ajax({
      url: `${process.env.VUE_APP_BACKEND_API_URL}/system_management/get_profile_types/`,
      method: "GET",
      headers: this.get_headers(),
    }).then(response => { this.profile_types = response?.data || []; });
  },
  edit_question(question) {
    this.validation_errors = {};
    this.editing_id = question.id;
    this.question_form = {
      text: question.question || "",
      number: question.question_number || "",
      type: question.question_type?.id || "",
      mandatory: !!question.is_mandatory,
      locked: !!question.is_locked,
      options: (question.options || []).map(option => ({ text: option.option || option.text || "" })),
    };
    this.show_create_panel = true;
    this.close_assignment();
  },
  edit_section(section) {
    this.validation_errors = {};
    this.editing_id = section.id;
    this.section_form = { name: section.name || "", locked: !!section.is_locked };
    this.show_create_panel = true;
    this.close_assignment();
  },
  edit_form(form) {
    this.validation_errors = {};
    this.editing_id = form.id;
    this.dynamic_form = { name: form.name || form.form_name || "", description: form.description || "" };
    this.show_create_panel = true;
    this.close_assignment();
  },
  cancel_edit() {
    this.validation_errors = {};
    this.editing_id = null;
    this.question_form = { text: "", number: "", type: "", mandatory: false, locked: false, options: [] };
    this.section_form = { name: "", locked: false };
    this.dynamic_form = { name: "", description: "" };
  },
  update_item() {
    if (this.active_tab === "questions") return this.update_question();
    if (this.active_tab === "sections") return this.update_section();
    return this.update_form();
  },
  update_question() {
    return this.update_json_item("update_question", {
      question_id: this.editing_id,
      update_values: {
        question: this.question_form.text,
        question_number: this.question_form.number,
        question_type: this.question_form.type,
        is_mandatory: this.question_form.mandatory,
        is_locked: this.question_form.locked,
        options: this.question_form.options.map(option => option.text.trim()).filter(Boolean),
      },
    }, "questions");
  },
  update_section() {
    return this.update_json_item("update_section", {
      section_id: this.editing_id,
      update_values: { name: this.section_form.name, is_locked: this.section_form.locked },
    }, "sections");
  },
  update_form() {
    return this.update_json_item("update_form", {
      form_id: this.editing_id,
      update_values: { name: this.dynamic_form.name, description: this.dynamic_form.description },
    }, "forms");
  },
  update_json_item(endpoint, payload, collection) {
    return $.ajax({
      url: `${process.env.VUE_APP_BACKEND_API_URL}/membership_management/${endpoint}/`,
      method: "POST",
      headers: this.get_json_headers(),
      data: JSON.stringify(payload),
    }).then(() => {
      this.fetch_collection(collection);
      this.show_create_panel = false;
      this.cancel_edit();
      show_success("Updated successfully");
    }).catch(() => Swal.fire({ icon: "error", title: "Unable to update item" }));
  },
  delete_item(collection, id) {
    const config = get_collection_config(collection);
    Swal.fire({ title: "Are you sure?", text: "This cannot be undone.", icon: "warning",
      showCancelButton: true, confirmButtonText: "Delete", confirmButtonColor: "#d33" }).then(result => {
        if (!result.isConfirmed) return;
        const payload = get_delete_payload(collection, id);
        $.ajax({
          url: `${process.env.VUE_APP_BACKEND_API_URL}/membership_management/${config.remove}/`,
          method: "DELETE",
          headers: this.get_json_headers(),
          data: JSON.stringify(payload),
        }).then(() => {
          remove_collection_item(this, collection, id);
          if (collection === "forms" && String(this.selected_preview_form_id) === String(id)) {
            this.selected_preview_form_id = this.forms[0]?.id || "";
            this.load_form_preview();
          }
          show_success("Deleted successfully");
        }).catch(() => Swal.fire({ icon: "error", title: "Unable to delete item" }));
      });
  },
};
